<script lang="ts">
	//Formatters
	//Design system components
	import {
		DeviceButton,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//services
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { hasControlRights } from 'src/helpers/user_helpers';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';

	export let device: Device,
		componentID: number,
		value: boolean,
		timer: {
			time: number;
			percentage: number;
		} = {
			time: 0,
			percentage: 100,
		},
		icon: string = null,
		hasTimer = false,
		isWide = false,
		disabled = false,
		variant: 'primary' | 'danger' | 'success' = 'primary';

	export let useV2Components = false;
	export let deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {
		size: 'm',
	};

	let waitForResponse = false;

	$: hasRights = hasControlRights(device);

	const handleToggle = async () => {
		if (waitForResponse) return;
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'boolean.set', {
			id: componentID,
			value: !value,
		});
		if (!res?.isok) {
			onSettingsResponse(res, device);
		}
		waitForResponse = false;
	};

	const getTimeAreaLabel = () => {
		if (hasTimer) {
			return (
				$format('label_timer', { default: 'Timer' }) +
				(timer?.time ? `: ${Math.floor(timer.time / 60)}m` : '')
			);
		}
		return null;
	};

	const a11yProps = {
		ariaLabel: value
			? $format('label_turn_off', { default: 'Turn off' })
			: $format('label_turn_on', { default: 'Turn on' }),
		timerAriaLabel: getTimeAreaLabel(),
		role: 'switch',
		'aria-checked': value,
		'aria-live': 'polite',
	};
</script>

{#key hasTimer}
	{#if useV2Components}
		<DeviceButtonV2
			id="devicebutton-hauqwg0"
			icon={icon && !icon.includes('toggle') ? icon : 'fas fa-power-off'}
			aria-label={value
				? $format('label_turn_off', { default: 'Turn off' })
				: $format('label_turn_on', { default: 'Turn on' })}
			{a11yProps}
			isLoading={waitForResponse}
			isActive={value}
			{hasTimer}
			{timer}
			size="l"
			{...deviceButtonV2Props}
			on:click={(e) => {
				e.stopPropagation();
				handleToggle();
			}}
			disabled={!hasRights}
		/>
	{:else}
		<DeviceButton
			id="devicebutton-hauqwg0"
			icon={icon && !icon.includes('toggle') ? icon : 'fas fa-power-off'}
			aria-label={value
				? $format('label_turn_off', { default: 'Turn off' })
				: $format('label_turn_on', { default: 'Turn on' })}
			{a11yProps}
			isLoading={waitForResponse}
			isActive={value}
			{hasTimer}
			{timer}
			{isWide}
			{variant}
			size={isWide ? 'l' : 'm'}
			on:click={(e) => {
				e.stopPropagation();
				handleToggle();
			}}
			disabled={!hasRights || disabled}
		/>
	{/if}
{/key}
